import type { AssistantModule } from "./types"

type Getter = () => AssistantModule | null

// Modules currently available. Pages register a getter so every step sees their latest state
export class ModuleRegistry {
  private getters = new Map<string, Getter>()
  private listeners = new Set<() => void>()

  register(id: string, get: Getter): () => void {
    this.getters.set(id, get)
    this.emit()
    return () => {
      if (this.getters.get(id) === get) {
        this.getters.delete(id)
        this.emit()
      }
    }
  }

  ids(): string[] {
    return [...this.getters.keys()]
  }

  list(): AssistantModule[] {
    const out: AssistantModule[] = []
    for (const get of this.getters.values()) {
      try {
        const m = get()
        if (m) out.push(m)
      } catch (e) {
        console.error("[assistant] module failed to build:", e)
      }
    }
    return out
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => { this.listeners.delete(fn) }
  }

  // Resolves when a module registers or unregisters, or after the timeout
  waitForChange(timeoutMs: number): Promise<void> {
    return new Promise(resolve => {
      const timer = setTimeout(done, timeoutMs)
      const off = this.subscribe(done)
      function done() {
        clearTimeout(timer)
        off()
        resolve()
      }
    })
  }

  // Resolves once `id` is registered, or false after the timeout
  async waitFor(id: string, timeoutMs: number): Promise<boolean> {
    const until = Date.now() + timeoutMs
    while (!this.getters.has(id)) {
      const left = until - Date.now()
      if (left <= 0) return false
      await this.waitForChange(left)
    }
    return true
  }

  private emit() {
    for (const fn of this.listeners) fn()
  }
}
